/**
 * D-196: your picture in the header opens this short menu: Admin first for admins (with what Admin › Today lists under
 * Needs a look), then View profile, Wallet, My items and Settings. A bottom sheet on phones, a popover on desktop.
 * Any row closes it and goes.
 */
import type { ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import { MEMBERSHIP_TIER_NAMES } from "@digu/shared";
import { Icon, Sheet } from "../components";
import { formatNumber } from "../lib/format";
import { useSession } from "../lib/session";
import { useDesktop } from "../lib/useDesktop";
import { HeaderPopover } from "./Notifications";
import "./you-menu.css";

interface MenuRow { key: string; icon: string; title: string; sub: string; to: string; count?: number; admin?: boolean }

export function youMenuRows(o: { admin: boolean; adminNeeds: number; tokens: number; boli: number }): MenuRow[] {
  return [
    ...(o.admin ? [{ key: "admin", icon: "shield-check", title: "Admin", sub: o.adminNeeds > 0 ? `${formatNumber(o.adminNeeds)} ${o.adminNeeds === 1 ? "thing needs" : "things need"} a look` : "Reports, verification, prizes and settings", to: "/admin", count: o.adminNeeds, admin: true }] : []),
    { key: "profile", icon: "user", title: "View profile", sub: "Your stats, badges and recent games", to: "/you" },
    { key: "wallet", icon: "coins", title: "Wallet", sub: `${formatNumber(o.tokens)} ${o.tokens === 1 ? "token" : "tokens"} · ${formatNumber(o.boli)} Boli`, to: "/wallet" },
    { key: "items", icon: "package", title: "My items", sub: "Looks, reactions, throws and tickets", to: "/shop?tab=mine" },
    { key: "settings", icon: "settings", title: "Settings", sub: "Theme, sound, notifications, account", to: "/you/settings" },
  ];
}

export function YouMenu({ open, onClose }: { open: boolean; onClose: () => void }): ReactElement | null {
  const { account, wallet, admin, adminNeeds } = useSession();
  const desktop = useDesktop();
  const navigate = useNavigate();
  if (!open) return null;
  const name = account?.name ?? "You";
  const tier = account?.membership.active ? account.membership.tier : null;
  const sub = `@${account?.username ?? ""}${tier ? ` · ${MEMBERSHIP_TIER_NAMES[tier]} member` : ""}${admin ? " · Admin" : ""}`;
  const rows = youMenuRows({ admin, adminNeeds, tokens: wallet?.tokens ?? 0, boli: wallet?.boli ?? 0 });
  const go = (to: string) => { onClose(); navigate(to); };
  const list = (
    <div className="you-menu" data-testid="you-menu">
      {rows.map((r) => (
        <button key={r.key} type="button" className={`you-menu__row${r.admin ? " you-menu__row--admin" : ""}`} onClick={() => go(r.to)} data-testid={`you-menu-${r.key}`}
          aria-label={r.count ? `${r.title}, ${formatNumber(r.count)} need a look` : r.title}>
          <span className="you-menu__icon" aria-hidden="true"><Icon name={r.icon} size={20} /></span>
          <span className="you-menu__text"><span className="body-b">{r.title}</span><span className="small text-secondary">{r.sub}</span></span>
          {r.count ? <span className="you-menu__count" aria-hidden="true">{r.count > 99 ? "99+" : formatNumber(r.count)}</span> : null}
          <Icon name="chevron-right" size={18} className="you-menu__chev" />
        </button>
      ))}
    </div>
  );
  if (desktop) return <HeaderPopover anchor="hd-me" title={name} sub={sub} onClose={onClose} testId="you-popover">{list}</HeaderPopover>;
  return <Sheet open onClose={onClose} title={name} sub={sub}>{list}</Sheet>;
}
